import React from 'react';
import { Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { V4_TABLE } from '../../theme/v4Compat';
import { Loader } from '../../elements';
import useAuthContext from '../../hooks/useAuthContext';
import { FeedRegistrationsService } from '../../hooks/FeedRegistrationsService';
import { AnimalType } from '../../models/animal';
import { FeedRegistrationAnimals, formatFeedAmount } from '../../models/feed-registration';
import { LoaderSize } from '../../constants/constants';
import { extractApiErrorMessage, notifications } from '../../utils';

export interface FeedRegistrationAnimalsPanelProps {
  registrationId: string;
}

/**
 * The animals one registration was split across, each linking to its own feed history. Loaded only
 * once a row is opened: the split is computed on read, one query per registration.
 */
const FeedRegistrationAnimalsPanel: React.FC<FeedRegistrationAnimalsPanelProps> = ({ registrationId }) => {
  const { t } = useTranslation();
  const { state } = useAuthContext();
  const navigate = useNavigate();
  const [result, setResult] = React.useState<FeedRegistrationAnimals | null>(null);
  const [loaded, setLoaded] = React.useState(false);

  const { farmId } = state;

  React.useEffect(() => {
    if (!farmId) return;

    let cancelled = false;

    new FeedRegistrationsService()
      .getRegistrationAnimals(farmId, registrationId)
      .then((animals) => {
        if (cancelled) return;
        setResult(animals);
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        if (cancelled) return;
        setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [farmId, registrationId, t]);

  if (!loaded) return <Loader isLoading size={LoaderSize.SMALL} />;
  if (!result) return null;

  if (result.animals.length === 0) {
    return (
      <p className="m-0 py-2 text-xs text-gray-500">
        {t('f-feed-reg-no-animals', { defaultValue: 'No animals were in this pen on this day' })}
      </p>
    );
  }

  return (
    <div className="py-2">
      <p className="m-0 mb-1 text-xs text-gray-600">
        {t('f-feed-reg-shared-by', {
          count: result.animals.length,
          amount: formatFeedAmount(result.allocatedGrams),
          defaultValue: 'Shared by {{count}} animals, {{amount}} each'
        })}
      </p>
      <Table size="small" aria-label="feed registration animals table" sx={V4_TABLE}>
        <TableHead>
          <TableRow>
            <TableCell>{t('f-com-animal-number')}</TableCell>
            <TableCell>{t('f-com-animal-type')}</TableCell>
            <TableCell align="right">{t('f-feed-reg-share')}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {result.animals.map((animal) => {
            const animalFeed = `/animal/${animal.animalId}?tab=feed`;

            return (
              <TableRow key={animal.animalId} hover className="cursor-pointer" onClick={() => navigate(animalFeed)}>
                <TableCell>
                  {/* A real link as well, so the animal can be opened in a new tab. */}
                  <Link
                    to={animalFeed}
                    onClick={(event) => event.stopPropagation()}
                    className="text-brand hover:underline"
                  >
                    {animal.number}
                  </Link>
                </TableCell>
                <TableCell>{AnimalType[animal.animalType]}</TableCell>
                <TableCell align="right">{formatFeedAmount(result.allocatedGrams)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default FeedRegistrationAnimalsPanel;
